import { Button, Flex, Loading, Typography } from '@iheartjane/reefer';
import { clsx } from 'clsx';
import type { Accept } from 'react-dropzone';
import { useDropzone } from 'react-dropzone';

import styles from './fileUploadArea.module.css';

export interface FileUploadAreaProps {
  /** Optional prop to specify accepted file types */
  accept?: Accept;

  /** When true, dropzone allows multiple files to be uploaded at once */
  allowMultiple?: boolean;

  /** Optional class name for custom styling */
  className?: string;

  /** Label to describe drag and drop option */
  dragLabel?: string;

  /** When true, component renders loading symbol */
  isLoading: boolean;

  /** Label to describe upload from computer option */
  label?: string;

  /** Function called when file(s) are dropped in the dropzone */
  onDrop: (acceptedFiles: Array<File>) => void;
}

export const FileUploadArea = ({
  accept,
  allowMultiple = false,
  className,
  onDrop,
  isLoading,
  label = 'Upload images from computer',
  dragLabel = 'Drag and drop images here',
}: FileUploadAreaProps) => {
  const { getRootProps, getInputProps, open } = useDropzone({
    accept,
    onDrop,
    noClick: true,
    multiple: allowMultiple,
  });

  return (
    <div className={clsx(styles['uploadArea'], className)} {...getRootProps()}>
      <Flex
        gap={12}
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        height="100%"
        aria-label={`${label} or ${dragLabel}`}
      >
        {isLoading ? (
          <div className={styles['loadingContainer']}>
            <Loading color="black" data-testid="loading-file-upload" />
          </div>
        ) : (
          <>
            <Button variant="secondary" label={label} onClick={open} />
            <input
              data-testid="file-upload"
              aria-label={label}
              {...getInputProps()}
            />
            <Typography color="grays-mid">or</Typography>
            <Typography color="grays-mid">{dragLabel}</Typography>
          </>
        )}
      </Flex>
    </div>
  );
};
